import React, { useState, useEffect } from 'react';

const ContactMASTIVIBES = () => {
  const [animatedTitle, setAnimatedTitle] = useState('');
  const [currentContact, setCurrentContact] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const fullTitle = "Connect with MASTIVIBES";
  
  const contactMethods = [
    {
      icon: '📧',
      title: 'Email Support',
      description: 'Get help with account, features, or platform questions',
      details: 'support@mastivibes.com',
      gradient: 'from-pink-500 to-purple-500'
    },
    {
      icon: '📞',
      title: 'Community Help',
      description: 'Speak with our community support specialists',
      details: '+1 (555) 123-4567',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: '💬',
      title: 'Live Chat',
      description: 'Real-time support for technical issues',
      details: 'Available 24/7',
      gradient: 'from-cyan-500 to-blue-500'
    },
  ];

  const faqs = [
    {
      question: 'How do I create custom profile fields?',
      answer: 'Go to your profile settings → Custom Fields → Add new field. You can create unlimited custom fields.'
    },
    {
      question: 'What is the maximum participants in group video calls?',
      answer: 'MASTIVIBES supports up to 100 participants in group video calls with HD quality and screen sharing.'
    },
    {
      question: 'Can I create private communities?',
      answer: 'Yes, you can create both public and private groups with custom privacy settings and membership controls.'
    },
    {
      question: 'Are voice rooms available on mobile?',
      answer: 'Yes, voice rooms work seamlessly across all platforms - mobile app, web, and desktop applications.'
    }
  ];

  // Social platform floating icons
  const socialFloatingIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-20 left-20', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-32 right-32', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-32 left-32', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-20 right-20', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/2 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setAnimatedTitle(fullTitle.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const contactInterval = setInterval(() => {
      setCurrentContact((prev) => (prev + 1) % contactMethods.length);
    }, 3000);

    return () => clearInterval(contactInterval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for reaching out! Our team will connect with you soon.');
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 pt-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(18)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.1}s`,
              opacity: 0.7
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${item.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${7 + index}s`
          }}
        >
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Animated Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30 mb-8 shadow-lg">
            <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-md"></span>
            <span className="text-white font-bold">We're Here for Your Social Journey!</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
              {animatedTitle}
            </span>
            <span className="animate-pulse text-yellow-300">|</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            Need help with communities, custom features, or platform questions? 
            Our support team is ready to help you create amazing social experiences.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Side - Content */}
          <div className="space-y-8">
            {/* Contact Methods */}
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl">
              <h3 className="text-2xl font-bold text-white mb-6">Get In Touch</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contactMethods.map((method, index) => (
                  <div 
                    key={index}
                    className={`p-6 rounded-2xl border-2 border-white/20 transition-all duration-500 transform ${
                      index === currentContact ? 'scale-105 bg-white/20 shadow-2xl' : 'bg-white/10'
                    } hover:scale-105 hover:bg-white/20 group shadow-lg`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${method.gradient} rounded-2xl flex items-center justify-center text-2xl text-white transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 ${index === currentContact ? 'animate-pulse' : ''} shadow-xl`}>
                        {method.icon}
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-lg">{method.title}</h4>
                        <p className="text-yellow-300 font-bold text-md">{method.details}</p>
                      </div>
                    </div>
                    <p className="text-white/80 text-sm mt-3 font-medium">
                      {method.description}
                    </p>
                    {index === currentContact && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping shadow-lg"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl">
              <h3 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white/10 rounded-2xl p-4 border-2 border-white/20 hover:bg-white/20 transition-all duration-300 group cursor-pointer shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-bold text-lg group-hover:text-yellow-300 transition-colors duration-300">
                        {faq.question}
                      </h4>
                      <span className="text-yellow-400 transform group-hover:rotate-180 transition-transform duration-300 text-xl">
                        ▼
                      </span>
                    </div>
                    <p className="text-white/80 mt-3 text-sm font-medium">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Stats */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { number: '24/7', label: 'Live Support', icon: '⚡' },
                { number: '500K+', label: 'Active Users', icon: '👥' },
                { number: '4.9', label: 'Platform Rating', icon: '⭐' },
                { number: '98%', label: 'Satisfaction', icon: '😊' }
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="text-center p-4 bg-white/10 backdrop-blur-lg rounded-xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 group shadow-lg"
                >
                  <div className="text-3xl mb-2 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="text-white/80 text-sm mt-1 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-6 border-2 border-white/25 shadow-2xl transform hover:scale-[1.02] transition-all duration-500 h-fit">
            <h3 className="text-2xl font-bold text-white mb-2">Send Us a Message</h3>
            <p className="text-white/80 mb-6 font-medium">We'll help you with your social journey</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="group">
                  <label className="block text-white font-bold mb-2 group-hover:text-yellow-300 transition-colors duration-300 text-sm">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 focus:bg-white/15 transition-all duration-300 backdrop-blur-lg text-sm font-medium"
                    placeholder="Enter your name"
                  />
                </div>

                <div className="group">
                  <label className="block text-white font-bold mb-2 group-hover:text-yellow-300 transition-colors duration-300 text-sm">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 focus:bg-white/15 transition-all duration-300 backdrop-blur-lg text-sm font-medium"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="group">
                <label className="block text-white font-bold mb-2 group-hover:text-yellow-300 transition-colors duration-300 text-sm">
                  What do you need help with? *
                </label>
                <div className="relative">
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gradient-to-r from-yellow-500/20 to-pink-500/20 border-2 border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 focus:bg-gradient-to-r focus:from-yellow-500/30 focus:to-pink-500/30 transition-all duration-300 backdrop-blur-lg text-sm font-medium appearance-none"
                  >
                    <option value="" className="bg-gray-800">Select a topic</option>
                    <option value="account" className="bg-gray-800">Account Setup</option>
                    <option value="groups" className="bg-gray-800">Group Creation</option>
                    <option value="video" className="bg-gray-800">Video Calls</option>
                    <option value="custom" className="bg-gray-800">Custom Fields</option>
                    <option value="technical" className="bg-gray-800">Technical Support</option>
                    <option value="other" className="bg-gray-800">Other</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-yellow-400">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="group">
                <label className="block text-white font-bold mb-2 group-hover:text-yellow-300 transition-colors duration-300 text-sm">
                  Your Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full bg-white/10 border-2 border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400 focus:bg-white/15 transition-all duration-300 backdrop-blur-lg resize-none text-sm font-medium"
                  placeholder="Tell us about your social journey or platform questions..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="relative group w-full"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-xl blur opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl text-sm">
                  <span className="mr-2 text-lg">✉️</span>
                  Send Your Message
                  <span className="ml-2 text-lg group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </button>

              <p className="text-white/60 text-xs text-center font-medium">
                We value your privacy and handle all data with care. No spam, ever.
              </p>
            </form>
          </div>
        </div>

        {/* Community Support Banner */}
        <div className="mt-16 bg-gradient-to-r from-yellow-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-6 border-2 border-yellow-400/30 text-center shadow-2xl">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
            <h3 className="text-xl font-bold text-white">Community & Support Channels</h3>
            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
          </div>
          <p className="text-white/80 text-sm mb-4 font-medium">
            Join our vibrant community or get help through our dedicated channels:
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="text-white font-bold text-lg bg-yellow-500/20 px-5 py-2 rounded-xl border-2 border-yellow-400/50 shadow-lg">
              📧 Email: support@mastivibes.com
            </div>
            <div className="text-white font-bold text-lg bg-pink-500/20 px-5 py-2 rounded-xl border-2 border-pink-400/50 shadow-lg">
              👥 Community: @mastivibeshelp
            </div>
          </div>
          <p className="text-white/60 text-xs mt-4 font-medium">
            Connect with other creators and get community-powered support 24/7!
          </p>
        </div>

        {/* Quick Action Buttons */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-xl blur opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
            <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl w-full">
              <span className="mr-2">🚀</span>
              Start Free Journey
              <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </button>
          <button className="bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center border-2 border-white/20 shadow-lg w-full">
            <span className="mr-2">📱</span>
            Get Mobile App
            <span className="ml-2 text-xs px-2 py-1 bg-yellow-500/30 rounded-full">BETA</span>
          </button>
          <button className="bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center border-2 border-white/20 shadow-lg w-full">
            <span className="mr-2">👥</span>
            Join Community
            <span className="ml-2 text-xs px-2 py-1 bg-pink-500/30 rounded-full">NEW</span>
          </button>
        </div>

        {/* Platform Social Links */}
        <div className="mt-12 bg-white/10 backdrop-blur-lg rounded-3xl p-6 border-2 border-white/20 shadow-xl">
          <h3 className="text-xl font-bold text-white text-center mb-6">Connect With Our Community</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '🐦', platform: 'Twitter', handle: '@mastivibes' },
              { icon: '📘', platform: 'Facebook', handle: '/mastivibes' },
              { icon: '📸', platform: 'Instagram', handle: '@mastivibes.official' },
              { icon: '🎬', platform: 'YouTube', handle: 'MASTIVIBES TV' }
            ].map((social, index) => (
              <div 
                key={index}
                className="text-center p-4 bg-white/5 rounded-2xl border-2 border-white/20 hover:bg-white/10 transition-all duration-300 hover:scale-105 group cursor-pointer shadow-lg"
              >
                <div className="text-4xl mb-2 transform group-hover:scale-110 transition-transform duration-300">
                  {social.icon}
                </div>
                <div className="text-white font-bold">{social.platform}</div>
                <div className="text-yellow-300 text-sm font-medium">{social.handle}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-15px) rotate(3deg) scale(1.1); 
          }
          66% { 
            transform: translateY(8px) rotate(-2deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default ContactMASTIVIBES;